// libs
import React, { useContext, useState, useCallback, memo } from "react";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";

// defs
import type { EMIResponseWidget, ProductDetailsHalfCardInfo, Analytics } from "@payment-client/interfaces/Checkout";

// utils
import { getCloudinaryUrl } from "@swiggy-private/js-utils";
import {
    sendEMIWidgetImpressionEvent,
    clickTnC,
} from "@payment-client/components/show-payments/components/review-bill-details-renderer/helpers/analytics";

// widgets
import ProductDetails from "./widgets/ProductDetails";
import TnCHalfCard from "./widgets/TnCHalfCard";
import EMILineItem from "./widgets/EMILineItem";
import TotalPayableItem from "./widgets/TotalPayable";

// contexts
import { AnalyticsContext } from "@swiggy-private/checkout-core";

// styled
import {
    BankInfo,
    BankInfoImage,
    SubText,
    BankInfoText,
    BankInfoDesc,
    Content,
    TnCButton,
    StyledCardContainer,
} from "./styled";

interface EMIDetailsProps {
    emi: EMIResponseWidget;
}

const containerPadding = { left: 16, right: 16, top: 36, bottom: 16 };
const imageSize = { width: 40, height: 40 };

const EMIDetails: React.FC<EMIDetailsProps> = ({ emi }) => {
    const { impressionEvent } = useContext(AnalyticsContext);
    const [showTnC, setShowTnC] = useState<boolean>(false);
    const [productDetails, setProductDetails] = useState<ProductDetailsHalfCardInfo | undefined>(undefined);

    const impressionEMIDetails = useCallback(
        (payload?: Analytics) => {
            sendEMIWidgetImpressionEvent(impressionEvent, payload);
        },
        [impressionEvent],
    );

    const handleTncClick = useCallback(() => {
        if (!showTnC) {
            clickTnC(impressionEvent);
        }
        setShowTnC(!showTnC);
    }, [showTnC, impressionEvent]);

    if (!emi.data || emi.data[0] == null) {
        return null;
    }

    const emiData = emi.data[0];
    const { bankLogoId, bankName, tenureDetails, emiSubtext, lineItems, totalPayable, termsAndConditions } = emiData;

    return (
        <StyledCardContainer customPadding={containerPadding}>
            <BankInfo>
                {bankLogoId && <BankInfoImage src={getCloudinaryUrl(bankLogoId, imageSize)} alt="emi-plan" />}
                <BankInfoText>
                    <Text variant="V2.body2.neutral.medium">{bankName}</Text>
                    <BankInfoDesc>
                        <Text variant="V2.body2.neutral.bold">{tenureDetails}</Text>
                        <SubText variant="V2.body2.neutral.medium" color="text_Med_Emphasis">
                            {emiSubtext}
                        </SubText>
                    </BankInfoDesc>
                </BankInfoText>
                <TnCButton underline buttonSize="small" color="primary" onClick={handleTncClick}>
                    T&C
                </TnCButton>
            </BankInfo>

            <Content>
                {lineItems.map((lineItem, index) => (
                    <EMILineItem
                        key={`emi-row-${index}`}
                        lineItem={lineItem}
                        setProductDetails={setProductDetails}
                        captureImpression={impressionEMIDetails}
                    />
                ))}
                <TotalPayableItem totalPayable={totalPayable} captureImpression={impressionEMIDetails} />
            </Content>

            {productDetails && (
                <ProductDetails
                    isOpen={!!productDetails}
                    onClose={() => setProductDetails(undefined)}
                    halfCardData={productDetails}
                />
            )}

            {showTnC && <TnCHalfCard halfCardData={termsAndConditions} isOpen={showTnC} onClose={handleTncClick} />}
        </StyledCardContainer>
    );
};

export default memo(EMIDetails);
